import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { useAddOrgIssueComment } from '../../../hooks/useOrgIssues.js';
import type { OrgIssueDetail } from '../../../lib/api/org-issues.js';
import { formatHistoryValue, personLabel } from '../../../lib/org-issues.js';

/** 日時を手元の表記にする。 @graph-connects none */
function when(iso: string): string {
  return new Date(iso).toLocaleString();
}

/**
 * 課題のコメントと履歴（列移動・優先度・担当・共有状態の変更）。
 *
 * コメントは課題を見られる人なら誰でも書ける（判定は API）。`canEdit` が無いときは入力欄を出さない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題のコメントを読み・足し、誰がいつ列・優先度・担当・共有状態を変えたかの履歴を見せる
 * @graph-connects useAddOrgIssueComment [calls] コメントの追加
 * @graph-connects formatHistoryValue [calls] 履歴の値の表示
 */
export function IssueActivity({ detail }: { detail: OrgIssueDetail }): ReactNode {
  const { t } = useTranslation();
  const comment = useAddOrgIssueComment(detail.board.id);
  const [body, setBody] = useState('');
  const ctx = { columns: detail.board.columns, people: detail.people, t };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('orgIssues.comments.heading')}
        </h3>
        {detail.comments.length === 0 && (
          <p className="text-sm text-[var(--color-text-muted)]">{t('orgIssues.comments.empty')}</p>
        )}
        <ul className="space-y-2">
          {detail.comments.map((item) => (
            <li key={item.id} className="rounded-md border border-[var(--color-border)] p-2">
              <p className="text-xs text-[var(--color-text-muted)]">
                {personLabel(detail.people, item.authorId)} · {when(item.createdAt)}
              </p>
              <p className="whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">
                {item.body}
              </p>
            </li>
          ))}
        </ul>
        {detail.permissions.canEdit && (
          <form
            className="space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              comment.mutate(
                { issueId: detail.issue.id, body },
                {
                  onSuccess: () => {
                    setBody('');
                  },
                },
              );
            }}
          >
            <Textarea
              aria-label={t('orgIssues.comments.placeholder')}
              placeholder={t('orgIssues.comments.placeholder')}
              rows={3}
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
              }}
            />
            {comment.isError && (
              <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
                {comment.error.message}
              </p>
            )}
            <Button type="submit" size="sm" loading={comment.isPending}>
              {t('orgIssues.comments.submit')}
            </Button>
          </form>
        )}
      </section>
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('orgIssues.history.heading')}
        </h3>
        {detail.history.length === 0 && (
          <p className="text-sm text-[var(--color-text-muted)]">{t('orgIssues.history.empty')}</p>
        )}
        <ul className="space-y-1">
          {detail.history.map((event) => (
            <li key={event.id} className="text-xs text-[var(--color-text-secondary)]">
              {when(event.at)} · {personLabel(detail.people, event.by)} ·{' '}
              {t(`orgIssues.history.fields.${event.field}`)}:{' '}
              {event.field === 'created'
                ? formatHistoryValue(event.field, event.to, ctx)
                : `${formatHistoryValue(event.field, event.from, ctx)} → ${formatHistoryValue(event.field, event.to, ctx)}`}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
